import { ReactChild } from 'react';

import { CheckedCalendarIcon, Label, TTranslator } from '@smartshell/ui-kit';
import dayjs from 'dayjs';
import { CellProps, Column } from 'react-table';

import { ClientsAvatar } from 'components';
import { TLocalesKeys } from 'localization';

import s from './Clients.module.scss';
import { TableBlock } from './TableBlock';
import { TClientsPaymentReportItem } from './types';

export const formatNumber = (num: number): string =>
  num.toLocaleString(undefined, { maximumFractionDigits: 2 });

export const getColumns = (
  currency: string,
  t: TTranslator<TLocalesKeys>
): Array<Column<TClientsPaymentReportItem>> => [
  {
    id: 'nickname',
    Header: t('client'),
    accessor: ({ user }) => user.nickname,
    Cell: ({
      row: {
        original: { user },
      },
    }: CellProps<TClientsPaymentReportItem>) => (
      <div className={s.nickname}>
        <ClientsAvatar avatar={user?.avatar_url} roles={user?.roles} size="s" />
        <span>{user.nickname}</span>
      </div>
    ),
    width: 230,
    disableResizing: true,
  },
  {
    id: 'created_at',
    Header: t('registration'),
    accessor: ({ user }) => dayjs(user.created_at).valueOf(),
    Cell: ({ value }: { value: number }): ReactChild => {
      const date = dayjs(value);

      return (
        <TableBlock
          Icon={CheckedCalendarIcon}
          topText={date.format('D MMM YYYY')}
          bottomText={date.format('HH:mm')}
        />
      );
    },
    width: 174,
    disableResizing: true,
  },
  {
    id: 'deposit',
    Header: t('client.deposit'),
    accessor: ({ user }) => user.deposit,
    Cell: ({ value }: { value: number }): ReactChild => {
      return (
        <Label
          iconName="SafeIcon"
          color="violet"
          text={`${formatNumber(value)} ${currency}`}
        />
      );
    },
    width: 149,
    disableResizing: true,
  },
  {
    id: 'user_discount',
    Header: t('discount'),
    accessor: ({ user }) => user.user_discount,
    Cell: ({ value }: { value: number }): ReactChild => {
      return `${value}%`;
    },
    width: 108,
    disableResizing: true,
  },
  {
    id: 'spacer',
    accessor: ({ user }) => user.id,
    Cell: () => <div className={s.verticalLine} />,
    disableSortBy: true,
    width: 10,
  },
  {
    id: 'total',
    Header: t('report.clients.spend.period'),
    accessor: ({ total }) => total.value,
    Cell: ({ value }: { value: number }): ReactChild => {
      return ` ${formatNumber(value)} ${currency}`;
    },
    width: 173,
    disableResizing: true,
  },
  {
    id: 'tariff',
    Header: t('report.clients.spend.tariffs'),
    accessor: ({ tariff }) => tariff.value,
    Cell: ({ value }: { value: number }): ReactChild => {
      return ` ${formatNumber(value)} ${currency}`;
    },
    width: 176,
    disableResizing: true,
  },
  {
    id: 'shop',
    Header: t('report.clients.spend.shop'),
    accessor: ({ shop }) => shop.value,
    Cell: ({ value }: { value: number }): ReactChild => {
      return ` ${formatNumber(value)} ${currency}`;
    },
    width: 179,
    disableResizing: true,
  },
  {
    id: 'average',
    Header: t('report.clients.averageReceipt'),
    accessor: ({ average }) => average.value,
    Cell: ({ value }: { value: number }): ReactChild => {
      return ` ${formatNumber(value)} ${currency}`;
    },
    width: 186,
    disableResizing: true,
  },
];
